import { useQuery } from '@apollo/client';
import type { FC } from 'react';
import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import type { DirectConection } from '@xing-com/crate-common-graphql-types';
import { useIsDesktop } from '@xing-com/crate-hooks-use-is-desktop';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { useNetworkTracking } from '@xing-com/crate-network-tracking';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';
import { GridContainer, GridItem } from '@xing-com/grid';
import { IconArrowRight, IconReaction } from '@xing-com/icons';
import { Section } from '@xing-com/layout-primitives';
import {
  mediaConfinedNavlessOnly,
  mediaNavExtended,
} from '@xing-com/layout-tokens';

import { NetworkDirectConnectionsDocument } from '../../graphql';

import { Styled } from './direct-connections.styled';

const DIRECT_CONNECTIONS_LIMIT_BIG = 8;
const DIRECT_CONNECTIONS_LIMIT_MEDIUM = 9;
const DIRECT_CONNECTIONS_LIMIT_SMALL = 4;

const URL_PROFILE_COMMON = '/profile/';

export const DirectConnections: FC = () => {
  const { trackNetworkNWTExtended } = useNetworkTracking();
  const intl = useIntl();
  const isDesktop = useIsDesktop();
  const loginUrl = useLoginAppUrl();
  const isTablet = useMatchMedia(mediaConfinedNavlessOnly);
  const isVeryWideScreen = useMatchMedia(mediaNavExtended);
  const [numberOfItems, setNumberOfItems] = React.useState<number>(0);
  const [layoutVariant, setLayoutVariant] = React.useState<
    'A' | 'B' | 'C' | undefined
  >('A');
  const [cardSize, setCardSize] = React.useState<'small' | 'large' | undefined>(
    'small'
  );
  const isRandomized = React.useRef(false);
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
  const [randomizedData, setRandomizedData] = React.useState([] as any);

  const { data, error } = useQuery(NetworkDirectConnectionsDocument, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const mustShowDirectConnections =
    !error &&
    data !== undefined &&
    (data?.networkDirectConnections?.collection?.length ?? 0) > 0;

  React.useEffect(() => {
    if (!isRandomized.current) {
      setRandomizedData(
        data?.networkDirectConnections?.collection
          ?.map((value: unknown) => value)
          .sort(() => Math.random() - 0.5) || []
      );

      if (randomizedData.length) {
        isRandomized.current = true;
      }
    }
  }, [data?.networkDirectConnections?.collection, randomizedData.length]);

  React.useEffect(() => {
    setNumberOfItems(
      isTablet
        ? DIRECT_CONNECTIONS_LIMIT_MEDIUM
        : isDesktop
          ? DIRECT_CONNECTIONS_LIMIT_BIG
          : DIRECT_CONNECTIONS_LIMIT_SMALL
    );
    setLayoutVariant(isDesktop ? 'A' : 'C');
    setCardSize(isVeryWideScreen ? 'large' : 'small');
  }, [isDesktop, isTablet, isVeryWideScreen]);

  const profileLinkTracking =
    '?sc_o=contacts_network_hpreco_profile_click&sc_o_PropActionOrigin=contacts_network_hpreco_module_reason_industry';
  const seeAllLinkTracking = `?sc_o=contacts_network_hpreco_show_more_click&signup_channel=minireg_network_hpreco`;
  const destParam =
    '&dest_url=' + encodeURIComponent('/network/recruiters?origin=network');
  const seeAllUrl = loginUrl + seeAllLinkTracking + destParam;
  const profileLinkUrl = profileLinkTracking + destParam;
  const hiringDetailsPage = '/network/hiring-details/';

  const onClickCta = (): void => {
    trackNetworkNWTExtended({
      event: 'opened',
      element: 'hpreco_see_all',
      moduleTitle: 'hiring_professional_recommendation',
    });
  };

  const onClickEntityCard = (
    itemIndex: number,
    hasHiringDetails: boolean
  ): void => {
    trackNetworkNWTExtended({
      event: 'opened',
      element: hasHiringDetails ? 'hpreco_hiring_details' : 'hpreco_profile',
      elementDetail: 'hpreco_module_reason_industry',
      moduleTitle: 'hiring_professional_recommendations',
      positionInModule: itemIndex,
    });
  };

  return mustShowDirectConnections ? (
    <Section data-qa="direct-connections">
      <Styled.Header variant="expanded" $isDesktop={isDesktop}>
        <div>
          <Styled.HeadlineCustom size={isDesktop ? 'xxxlarge' : 'xxlarge'}>
            <FormattedMessage id="NETWORK_LOGGED_OUT_DIRECT_CONNECTIONS_HEADLINE" />
          </Styled.HeadlineCustom>
          <Styled.Subline size={isDesktop ? 'large' : 'medium'}>
            <FormattedMessage id="NETWORK_LOGGED_OUT_DIRECT_CONNECTIONS_SUBLINE" />
          </Styled.Subline>
        </div>
        <Styled.ActionContainer
          to={seeAllUrl + seeAllLinkTracking}
          onClick={onClickCta}
        >
          <Styled.ActionCopy size="medium">
            <FormattedMessage id="NETWORK_LOGGED_OUT_DIRECT_CONNECTIONS_LINK" />
          </Styled.ActionCopy>
          <IconArrowRight width={24} height={24} />
        </Styled.ActionContainer>
      </Styled.Header>
      <GridContainer variant="expanded" data-qa="direct-connections-container">
        {randomizedData &&
          randomizedData.slice(0, numberOfItems).map(
            (_: DirectConection, i: number) =>
              !!randomizedData && (
                <GridItem
                  span={12}
                  spanXSmall={12}
                  spanMedium={4}
                  spanLarge={3}
                  key={i}
                >
                  <Styled.EntityCardCustom
                    layout={layoutVariant}
                    size={cardSize}
                    headline={randomizedData[i]?.xingId?.displayName ?? ''}
                    copy1={
                      randomizedData?.[i]?.xingId?.occupations?.[0]?.subline ??
                      ''
                    }
                    copy2={
                      randomizedData?.[i]?.xingId?.occupations?.[0]?.headline ??
                      ''
                    }
                    description={
                      intl.formatMessage({
                        id: 'NETWORK_LOGGED_OUT_DIRECT_CONNECTIONS_INDUSTRY_DESCRIPTION',
                      }) +
                      ' ' +
                      intl.formatMessage({
                        id: randomizedData?.[i]?.recruiterInsights
                          ?.localizationKey,
                      })
                    }
                    descriptionIcon={
                      randomizedData?.[i]?.recruiterInsights?.localizationKey
                        ? IconReaction
                        : undefined
                    }
                    profileImage={{
                      profileName: randomizedData?.[i]?.xingId?.displayName,
                      src: randomizedData?.[i]?.xingId?.profileImage?.[0]?.url,
                    }}
                    button={
                      randomizedData?.[i]?.xingId?.hasHiringDetails
                        ? {
                            children: (
                              <FormattedMessage id="NETWORK_DIRECT_CONNECTIONS_HIRING_HIGHLIGHTS_CTA" />
                            ),
                            to:
                              hiringDetailsPage +
                              randomizedData?.[i]?.xingId?.pageName +
                              '?origin=network',
                          }
                        : {
                            children: (
                              <FormattedMessage id="NETWORK_LOGGED_OUT_DIRECT_CONNECTIONS_CTA" />
                            ),
                            to:
                              URL_PROFILE_COMMON +
                              randomizedData?.[i]?.xingId?.pageName +
                              profileLinkUrl,
                          }
                    }
                    markers={[
                      {
                        variant: 'highlight',
                        children: intl.formatMessage({
                          id: 'NETWORK_LOGGED_OUT_DIRECT_CONNECTIONS_TAG',
                        }),
                      },
                    ]}
                    to={
                      URL_PROFILE_COMMON +
                      randomizedData?.[i]?.xingId?.pageName +
                      profileLinkUrl
                    }
                    onClick={() =>
                      onClickEntityCard(
                        i,
                        randomizedData?.[i]?.xingId?.hasHiringDetails
                      )
                    }
                  />
                </GridItem>
              )
          )}
      </GridContainer>
    </Section>
  ) : null;
};
